/** Sign in by email link (ADR-0024): the answer is always the same confirmation, so the page never says whether an address is known. */
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { ErrorBody } from "@bsim/contracts";
import type { RouteCall } from "../api/types";
import { Link, useLocation } from "../shell/history";
import { GxErrorBanner } from "../theme/primitives";
import { GxTextField } from "../theme/TextField";
import { AuthLayout } from "./AuthLayout";
import { authHref, hasSignedInBefore, JOIN_PATH, safeReturnTo } from "./returnTo";

export function SignInPage({ requestLink }: { readonly requestLink: RouteCall<"authRequestMagicLink"> }) {
  const returnTo = safeReturnTo(new URLSearchParams(useLocation().search).get("returnTo"));
  // Before sign-in the page cannot tell a solo run from a team's: it names the facilitator only when the invite led here from a team's page.
  const teamInvite = returnTo?.startsWith("/c/") === true;
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const [pending, setPending] = useState(false);
  const sentHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (sentTo !== null) sentHeading.current?.focus(); }, [sentTo]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const address = email.trim();
    const result = await requestLink(undefined, { email: address, ...(returnTo === null ? {} : { returnTo }) });
    setPending(false);
    if (result.ok) setSentTo(address); else setError(result.error);
  };

  const joinLink = <Link href={authHref(JOIN_PATH, returnTo)} className="gx-auth-link" data-testid="auth.joinLink">Have a join code?</Link>;

  if (sentTo !== null) {
    return (
      <AuthLayout>
        <div role="status" data-testid="auth.sent">
          <div className="gx-eyebrow">Sign in</div>
          <h1 className="gx-h1 gx-auth-h" tabIndex={-1} ref={sentHeading}>Check your email</h1>
          <p className="gx-auth-lead">If <b>{sentTo}</b> is on a program, a sign-in link is on its way. It works once and expires in 15 minutes.</p>
          <p className="gx-auth-lead">{teamInvite ? "Not on a program yet? Ask your facilitator for an invitation or a join code." : "Check that you used the address you were invited with, or use a join code."}</p>
        </div>
        <div className="gx-auth-acts">
          <button type="button" className="gx-btn gx-btn--ghost" onClick={() => setSentTo(null)}>Use a different email</button>
        </div>
        <p className="gx-auth-join">{joinLink}</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="gx-eyebrow">Sign in</div>
      <h1 className="gx-h1 gx-auth-h">{hasSignedInBefore() ? "Welcome back" : "Sign in to your bank"}</h1>
      <p className="gx-auth-lead">Enter the email you were invited with. We&apos;ll send you a link that signs you in, with no password to remember.</p>
      <form className="gx-auth-form" onSubmit={(e) => { void submit(e); }} data-testid="auth.form">
        {error === null ? null : <GxErrorBanner error={error} />}
        <GxTextField id="auth-email" label="Email" type="email" name="email" autoComplete="email" inputMode="email" required autoFocus value={email} invalid={error !== null} onChange={(e) => setEmail(e.target.value)} />
        <button type="submit" className="gx-btn gx-btn--primary gx-btn--lg gx-auth-submit" disabled={pending || email.trim() === ""}>{pending ? "Sending…" : "Send me a link"}</button>
      </form>
      <p className="gx-auth-join">{joinLink}</p>
    </AuthLayout>
  );
}
